import { type CaraWorkspaceFieldsFragment, type IntegrationType } from '@oysterjs/ais/api/graphql';
import { integrationName } from '@oysterjs/ais/lib/integrations';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

/** A SEARCH_INTEGRATION call as the workspace query selects it; output is null until it ends. */
export type SearchIntegrationTool = NonNullable<
  NonNullable<StageEvent['toolCall']>['searchIntegrationTool']
>;

/** A filter node as the tool call records it: a group of children, or a leaf. */
export type SearchFilter = {
  negate?: boolean | null;
  field?: string | null;
  op?: string | null;
  value?: string | null;
  values?: readonly string[] | null;
  and?: readonly SearchFilter[] | null;
  or?: readonly SearchFilter[] | null;
};

/**
 * A filter shaped for display: "all" when it has no condition, a group's
 * conjunction resolved to all or any, a group of one un-negated child replaced
 * by that child, and a leaf's value and values merged into one list. Each leaf
 * carries its place in the step's reading order.
 */
export type FilterView = { kind: 'all' } | ConditionView;

/** The groups and leaves of a filter that has conditions. */
type ConditionView =
  | { kind: 'group'; match: 'all' | 'any'; negate: boolean; children: ConditionView[] }
  | { kind: 'leaf'; negate: boolean; field: string; op: string; values: string[]; order: number };

const conditionView = (node: SearchFilter, next: () => number): ConditionView => {
  const match = node.and?.length ? 'all' : node.or?.length ? 'any' : null;
  if (match) {
    const children = ((match === 'all' ? node.and : node.or) ?? []).map((child) =>
      conditionView(child, next)
    );
    if (children.length === 1 && !node.negate) return children[0];
    return { kind: 'group', match, negate: !!node.negate, children };
  }
  const values = node.values?.length ? [...node.values] : node.value ? [node.value] : [];
  return {
    kind: 'leaf',
    negate: !!node.negate,
    field: node.field ?? '',
    op: (node.op ?? '').toUpperCase(),
    values,
    order: next()
  };
};

/**
 * The tool rejects a filter with no condition, and the step reads it as
 * "all <records>", which is what the model asked for.
 */
const filterView = (node: SearchFilter, next: () => number): FilterView =>
  node.field || node.and?.length || node.or?.length ? conditionView(node, next) : { kind: 'all' };

/**
 * The step's input shaped for display. Leaves are numbered across every
 * filter in the step, the root's first and then each include's.
 */
export const searchView = (input: SearchIntegrationTool['input']) => {
  let leaves = 0;
  const next = () => leaves++;
  return {
    where: filterView(input.where, next),
    includes: (input.include ?? []).map((include) => ({
      ...include,
      relation: include.relation.replace(/_/g, ' '),
      where: include.where ? filterView(include.where, next) : null
    }))
  };
};

const ENTITY_PLURALS = new Map([
  ['ACCOUNT', 'accounts'],
  ['POLICY', 'policies'],
  ['CLAIM', 'claims'],
  ['CERTIFICATE', 'certificates'],
  ['ACTIVITY', 'activities'],
  ['OPPORTUNITY', 'opportunities']
]);

/** An entity's name for count records; each entity's singular is its name lowercased. */
export const entityLabel = (find: string | null | undefined, count = 2): string => {
  const key = (find ?? '').toUpperCase();
  if (count === 1 && key) return key.toLowerCase();
  return ENTITY_PLURALS.get(key) ?? (key.toLowerCase() || 'records');
};

/** Fields whose snake_case name reads worse than a label. */
const FIELD_LABELS = new Map([
  ['external_id', 'AMS ID'],
  ['id', 'Cara ID'],
  ['lob_code', 'line of business'],
  ['status_group', 'status'],
  ['task_status_group', 'task status']
]);

export const fieldLabel = (field: string): string => {
  const label = FIELD_LABELS.get(field);
  if (label) return label;
  return field
    .replace(/_external_id$/, ' AMS ID')
    .replace(/_id$/, ' Cara ID')
    .replace(/_/g, ' ');
};

/**
 * Each operator as it reads plainly and under a negation, so a negated leaf
 * reads as one condition.
 */
const OP_LABELS = new Map<string, [plain: string, negated: string]>([
  ['EQ', ['is', 'is not']],
  ['NEQ', ['is not', 'is']],
  ['IN', ['is any of', 'is none of']],
  ['NOT_IN', ['is none of', 'is any of']],
  ['CONTAINS', ['contains', "doesn't contain"]],
  ['LT', ['<', 'not <']],
  ['LTE', ['≤', 'not ≤']],
  ['GT', ['>', 'not >']],
  ['GTE', ['≥', 'not ≥']],
  ['IS_NULL', ['is empty', 'is set']],
  ['IS_NOT_NULL', ['is set', 'is empty']]
]);

export const opLabel = (op: string, negate = false): string => {
  const labels = OP_LABELS.get(op);
  if (labels) return labels[negate ? 1 : 0];
  return negate ? `not ${op.toLowerCase()}` : op.toLowerCase();
};

/** A value as the model wrote it, with a relative date spelled out. */
export const valueLabel = (value: string): string => {
  const relative = /^today(?:([+-])(\d{1,4})d)?$/i.exec(value.trim());
  if (!relative) return value;
  const [, sign, days] = relative;
  if (!sign || days === '0') return 'today';
  const unit = days === '1' ? 'day' : 'days';
  return sign === '+' ? `${days} ${unit} from now` : `${days} ${unit} ago`;
};

export const bundleLabel = (bundle: string): string => bundle.replace(/^@/, '').replace(/_/g, ' ');

/** One result as the tool returns it: its IDs and a key per requested bundle, such as summary. */
type Result = Record<string, unknown>;

/** Summary keys that name a record, in the order they are preferred. */
const LABEL_KEYS = [
  'nameFull',
  'dba',
  'policyNumber',
  'carrierClaimNumber',
  'agencyClaimNumber',
  'subject',
  'title',
  'description',
  'lookupCode'
];

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** How a result is named: its summary's name, number or subject, else its AMS or Cara ID. */
export const resultLabel = (result: Result): string => {
  const summary = (result.summary ?? {}) as Result;
  for (const key of LABEL_KEYS) {
    const value = text(summary[key]);
    if (value) return value;
  }
  return text(result.externalId) || text(result.id) || 'Record';
};

/** A second fact that tells results apart: a policy's carrier, an account's lookup code. */
export const resultDetail = (result: Result): string => {
  const summary = (result.summary ?? {}) as Result;
  return (
    text(summary.carrierName) || text(summary.lineOfBusinessSummary) || text(summary.lookupCode)
  );
};

/** Where a call stands: running, its input rejected, failed, or answered. */
export type SearchOutcome =
  | { kind: 'pending' }
  | { kind: 'rejected' }
  | { kind: 'failed' }
  | { kind: 'results'; source: string; results: Result[]; hasMore: boolean };

/**
 * Codes of the one error that is not about the input: the AMS could not be
 * read at all. Calls recorded while only Applied Epic was read live carry
 * EPIC_UNAVAILABLE.
 */
const UNAVAILABLE = new Set(['LIVE_UNAVAILABLE', 'EPIC_UNAVAILABLE']);

/** The output arrives only when the call ends, so a call with none is still running. */
export const searchOutcome = (tool: SearchIntegrationTool, failed: boolean): SearchOutcome => {
  const output = tool.output;
  if (failed || UNAVAILABLE.has(output?.error?.code ?? '')) return { kind: 'failed' };
  if (output?.error) return { kind: 'rejected' };
  if (!output) return { kind: 'pending' };
  return {
    kind: 'results',
    source: output.source ?? '',
    results: output.results ?? [],
    hasMore: !!output.hasMore
  };
};

/** Where live results were read from: the systems their records name. */
export const liveSource = (results: Result[]): string => {
  const names = new Set(
    results
      .map((r) => text(r.integrationType))
      .filter(Boolean)
      .map((t) => integrationName(t as IntegrationType))
  );
  return names.size ? `live from ${[...names].join(' and ')}` : 'read live';
};

/** The step's title: what kind of record is being searched for. */
export const searchIntegrationTitle = (tool: SearchIntegrationTool | null | undefined): string =>
  `Searching ${entityLabel(tool?.input?.find)}`;
